/**
 * Email Center — three tabs for GST / PAYE reminder email flows.
 * Tabs: Send · Templates · Sent log.
 * All emails ride the firm's connected Outlook mailbox; nothing in the
 * client-facing output mentions ClientOps.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
    Loader2, Search, Send, Save, Eye, FileEdit, Users, Mail,
    Copy, Archive, History, X, Plus, Paperclip, Trash2,
    CheckCircle2, AlertTriangle, Flag, Upload,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";

const MERGE_FIELDS = [
    "client_first_name", "entity_name", "tax_type",
    "period_start", "period_end", "due_date", "deadline",
    "amount", "missing_items", "prosaic_link", "bank_guide_link", "sender_name",
];
const MANUAL_FIELD_LABELS = {
    period_start: "Period start", period_end: "Period end",
    due_date: "Due date", deadline: "Deadline",
    amount: "Amount", missing_items: "Missing items",
};

export default function EmailCenter() {
    const [tab, setTab] = useState("send");
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";
    return (
        <div className="space-y-4" data-testid="email-center">
            <header>
                <h1 className="text-xl font-semibold text-[#0F1115]">Email Center</h1>
                <p className="text-[12.5px] text-[#5E636E]">GST and PAYE reminders — sent from the firm's Outlook mailbox, branded <b>NZ Taxation Limited</b>.</p>
            </header>
            <nav className="flex gap-2 border-b border-[#E4E4E7]">
                {[["send", "Send"], ["templates", "Templates"], ["sent", "Sent log"]].map(([k, label]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`text-[13px] px-3 py-2 -mb-px border-b-2 ${tab === k ? "border-[#0F1115] text-[#0F1115] font-medium" : "border-transparent text-[#5E636E] hover:text-[#0F1115]"}`}
                        data-testid={`email-tab-${k}`}>
                        {label}
                    </button>
                ))}
            </nav>
            {tab === "send" && <SendTab isAdmin={isAdmin} />}
            {tab === "templates" && <TemplatesTab isAdmin={isAdmin} />}
            {tab === "sent" && <SentLogTab />}
        </div>
    );
}

// ---------- Send tab ----------

function SendTab({ isAdmin }) {
    const [clients, setClients] = useState([]);
    const [picked, setPicked] = useState([]); // array of client objects
    const [filter, setFilter] = useState("");
    const [templates, setTemplates] = useState([]);
    const [tplId, setTplId] = useState("");
    const tpl = templates.find((t) => t.id === tplId);
    const [fields, setFields] = useState({});   // per-field manual values (shared across the batch)
    const [overrides, setOverrides] = useState({}); // { client_id: { subject, body, attachments:[] } }
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewIdx, setPreviewIdx] = useState(0);
    const [previewData, setPreviewData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [ccFirm, setCcFirm] = useState(true);
    const [saveAsNew, setSaveAsNew] = useState(false);
    const [sending, setSending] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);

    useEffect(() => {
        api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
        api.get("/email-center/templates").then(({ data }) => setTemplates(data || [])).catch(() => {});
    }, []);

    // Auto-fill per-client date defaults when the template or picked list changes.
    useEffect(() => {
        if (!tpl || !["GST", "PAYE"].includes(tpl.category) || !picked.length) return;
        let cancelled = false;
        (async () => {
            for (const c of picked) {
                const existing = overrides[c.id]?.fields || {};
                if (existing.period_start || existing.due_date) continue;
                try {
                    const { data } = await api.get("/email-center/date-defaults", { params: { client_id: c.id, category: tpl.category } });
                    if (cancelled) return;
                    setOverrides((o) => ({
                        ...o,
                        [c.id]: { ...(o[c.id] || {}), fields: { ...(o[c.id]?.fields || {}), ...(data.defaults || {}) }, dateSource: "default" },
                    }));
                } catch { /* ignore */ }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tpl?.id, tpl?.category, picked.map((c) => c.id).join(",")]);

    const filtered = useMemo(() => {
        if (!filter) return clients.slice(0, 100);
        const q = filter.toLowerCase();
        return clients.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.contact_email || "").toLowerCase().includes(q)).slice(0, 100);
    }, [clients, filter]);

    const toggle = (c) => setPicked((p) => p.find((x) => x.id === c.id) ? p.filter((x) => x.id !== c.id) : [...p, c]);
    const removePick = (id) => setPicked((p) => p.filter((x) => x.id !== id));

    const manualRequired = tpl?.required_fields || [];

    async function openPreviewFor(idx) {
        const c = picked[idx]; if (!c) return;
        const ov = overrides[c.id] || {};
        const mergedFields = { ...fields, ...(ov.fields || {}) };
        const body = {
            template_id: tplId || null,
            client_id: c.id,
            fields: mergedFields,
            override_subject: ov.subject,
            override_body: ov.body,
        };
        try {
            const { data } = await api.post("/email-center/preview", body);
            setPreviewData({ ...data, client: c, index: idx });
            setPreviewIdx(idx);
            setEditing(false);
            setPreviewOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    function saveEditedBody(subject, body) {
        const c = picked[previewIdx]; if (!c) return;
        setOverrides((o) => ({ ...o, [c.id]: { ...(o[c.id] || {}), subject, body } }));
        setEditing(false);
        toast.success("Edit saved for this email");
    }

    function setAttachmentsFor(cid, atts) {
        setOverrides((o) => ({ ...o, [cid]: { ...(o[cid] || {}), attachments: atts } }));
    }

    function setDateFieldFor(cid, key, value) {
        setOverrides((o) => {
            const prev = o[cid] || {};
            const nextFields = { ...(prev.fields || {}), [key]: value };
            // Keep due_date and deadline in sync (templates use either)
            if (key === "due_date") nextFields.deadline = value;
            else if (key === "deadline") nextFields.due_date = value;
            return { ...o, [cid]: { ...prev, fields: nextFields, dateSource: "manual" } };
        });
    }

    async function resetDatesFor(cid) {
        if (!tpl || !["GST", "PAYE"].includes(tpl.category)) return;
        try {
            const { data } = await api.get("/email-center/date-defaults", { params: { client_id: cid, category: tpl.category } });
            setOverrides((o) => ({
                ...o,
                [cid]: { ...(o[cid] || {}), fields: { ...(o[cid]?.fields || {}), ...(data.defaults || {}) }, dateSource: "default" },
            }));
            toast.success("Dates reset to default");
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function doSend({ isTest }) {
        if (!picked.length) { toast.error("Pick at least one client"); return; }
        if (!tplId && !Object.values(overrides).some((o) => o.subject || o.body)) { toast.error("Pick a template"); return; }
        setSending(true);
        try {
            const items = picked.map((c) => {
                const ov = overrides[c.id] || {};
                return {
                    client_id: c.id,
                    fields: { ...fields, ...(ov.fields || {}) },
                    attachments: ov.attachments || [],
                    override_subject: ov.subject || null,
                    override_body: ov.body || null,
                    skip: ov.skip || false,
                };
            });
            const { data } = await api.post("/email-center/send", {
                template_id: tplId || null,
                items,
                cc_firm: ccFirm,
                save_as_new_template_version: saveAsNew && isAdmin,
                is_test: isTest,
            });
            const blocked = (data.results || []).filter((r) => r.status === "blocked");
            if (blocked.length) {
                toast.error(`${data.sent}/${data.total} sent. ${blocked.length} blocked — check preview for missing fields.`);
            } else {
                toast.success(`${data.sent}/${data.total} ${isTest ? "test " : ""}emails sent`);
            }
            if (!isTest && data.sent > 0) {
                setPicked([]); setOverrides({}); setFields({});
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    }

    // Build per-client "status" for the review queue
    const rows = picked.map((c) => {
        const ov = overrides[c.id] || {};
        const merged = { ...fields, ...(ov.fields || {}) };
        const missing = manualRequired.filter((k) => !(merged[k]));
        const needsAtt = tpl?.required_attachment && !(ov.attachments || []).length;
        let status = "ready", reason = "";
        if (ov.skip) { status = "skip"; reason = "Skipped by admin"; }
        else if (!c.contact_email) { status = "missing"; reason = "no email on file"; }
        else if (missing.length) { status = "missing"; reason = `missing ${missing.join(", ")}`; }
        else if (needsAtt) { status = "missing"; reason = "attachment required"; }
        return { client: c, status, reason, override: ov };
    });
    const readyCount = rows.filter((r) => r.status === "ready").length;

    return (
        <div className="grid lg:grid-cols-[1fr_380px] gap-5">
            <div className="space-y-4">
                {/* Step 1 — pick clients */}
                <Card title="Step 1 — Pick client(s)">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="relative flex-1">
                            <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-[#5E636E]" />
                            <Input placeholder="Search client name / email…" value={filter} onChange={(e) => setFilter(e.target.value)} className="pl-8 h-8 text-[13px]" data-testid="ec-search" />
                        </div>
                        <span className="text-[11.5px] text-[#5E636E]">{picked.length} picked</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto rounded-md border border-[#E4E4E7] bg-white divide-y divide-[#EEEDE7]">
                        {filtered.map((c) => (
                            <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] cursor-pointer hover:bg-zinc-50">
                                <input type="checkbox" checked={!!picked.find((x) => x.id === c.id)} onChange={() => toggle(c)} data-testid={`ec-client-${c.id}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-[#0F1115] truncate">{c.name}</div>
                                    <div className="text-[11px] text-[#5E636E] truncate">{c.contact_email || "no email on file"}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </Card>

                {/* Step 2 — template */}
                <Card title="Step 2 — Pick a template">
                    <select value={tplId} onChange={(e) => setTplId(e.target.value)} className="w-full h-9 rounded-md border border-[#E4E4E7] bg-white px-3 text-[13px]" data-testid="ec-template">
                        <option value="">— Select template —</option>
                        {["GST", "PAYE", "WELCOME", "other"].map((cat) => {
                            const inCat = templates.filter((t) => t.category === cat);
                            if (!inCat.length) return null;
                            return (
                                <optgroup key={cat} label={cat}>
                                    {inCat.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </optgroup>
                            );
                        })}
                    </select>
                    {tpl?.category === "WELCOME" && (
                        <div className="mt-2 flex items-center gap-2" data-testid="ec-welcome-switch">
                            <span className="text-[11.5px] text-[#5E636E]">Client type:</span>
                            {[
                                { k: "Sole Trader", match: "Sole" },
                                { k: "Company", match: "Company" },
                            ].map((o) => {
                                const target = templates.find((t) => t.category === "WELCOME" && t.name.includes(o.match));
                                const active = tpl?.name.includes(o.match);
                                return (
                                    <button key={o.k}
                                        onClick={() => target && setTplId(target.id)}
                                        className={`text-[12px] px-2.5 h-7 rounded-full border ${active ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:border-[#0F1115]"}`}
                                        data-testid={`ec-welcome-${o.match.toLowerCase()}`}>
                                        {o.k}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {tpl?.required_attachment && <div className="mt-2 text-[11.5px] text-amber-700">Attachment required per client (upload below).</div>}
                </Card>

                {/* Step 3 — fields */}
                {tpl && (
                    <Card title="Step 3 — Fill the manual fields" subtitle="Amount & missing items apply to all picked clients. Dates are auto-filled per client below (editable).">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {["amount", "missing_items"].map((k) => {
                                const req = manualRequired.includes(k);
                                const miss = req && !fields[k];
                                if (k === "amount") {
                                    return (
                                        <div key={k}>
                                            <Label className={miss ? "text-red-700" : ""}>
                                                {MANUAL_FIELD_LABELS[k]}{req && <span className="text-red-600"> *</span>}
                                            </Label>
                                            <div className="relative mt-1">
                                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12.5px] text-[#5E636E] pointer-events-none">$</span>
                                                <Input
                                                    value={(fields[k] || "").replace(/^\$/, "")}
                                                    onChange={(e) => {
                                                        const raw = e.target.value.replace(/^\$/, "");
                                                        setFields({ ...fields, [k]: raw ? `$${raw}` : "" });
                                                    }}
                                                    placeholder="0.00"
                                                    className={`pl-6 h-8 text-[12.5px] ${miss ? "border-red-400" : ""}`}
                                                    data-testid={`ec-field-${k}`}
                                                />
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={k}>
                                        <Label className={miss ? "text-red-700" : ""}>
                                            {MANUAL_FIELD_LABELS[k]}{req && <span className="text-red-600"> *</span>}
                                        </Label>
                                        <Input value={fields[k] || ""} onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                                            className={`mt-1 h-8 text-[12.5px] ${miss ? "border-red-400" : ""}`} data-testid={`ec-field-${k}`} />
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                )}

                {/* Review queue / per-client attachments */}
                {picked.length > 0 && (
                    <Card title="Review & attachments per client" subtitle={`${readyCount} / ${picked.length} ready to send`}>
                        <div className="rounded-md border border-[#E4E4E7] divide-y divide-[#EEEDE7] bg-white">
                            {rows.map(({ client: c, status, reason, override }, idx) => (
                                <div key={c.id} className="px-3 py-2" data-testid={`ec-row-${c.id}`}>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-[13px] text-[#0F1115] truncate">{c.name}</div>
                                            <div className="text-[11px] text-[#5E636E] truncate">{c.contact_email || "no email on file"}</div>
                                        </div>
                                        <StatusChip status={status} />
                                        <Button size="sm" variant="outline" className="h-7 text-[11.5px]" onClick={() => openPreviewFor(idx)} data-testid={`ec-preview-${c.id}`}>
                                            <Eye className="h-3 w-3 mr-1" />Preview
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-7 text-[11.5px]" onClick={() => setOverrides((o) => ({ ...o, [c.id]: { ...(o[c.id] || {}), skip: !override.skip } }))}>
                                            {override.skip ? "Unskip" : "Skip"}
                                        </Button>
                                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5E636E] hover:text-red-700" onClick={() => removePick(c.id)}>
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                    {status === "missing" && <div className="mt-1 text-[11px] text-red-700">⚠ {reason}</div>}
                                    {tpl && ["GST", "PAYE"].includes(tpl.category) && (
                                        <PerClientDates
                                            category={tpl.category}
                                            gstFrequency={c.gst_frequency}
                                            fields={override.fields || {}}
                                            source={override.dateSource}
                                            onChange={(k, v) => setDateFieldFor(c.id, k, v)}
                                            onReset={() => resetDatesFor(c.id)}
                                            testIdBase={`ec-dates-${c.id}`}
                                        />
                                    )}
                                    <PerClientAttachments
                                        attachments={override.attachments || []}
                                        required={!!tpl?.required_attachment}
                                        onChange={(atts) => setAttachmentsFor(c.id, atts)}
                                        testIdBase={`ec-att-${c.id}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </Card>
                )}
            </div>

            {/* Right sidebar — actions */}
            <aside className="space-y-3">
                <div className="rounded-md border border-[#E4E4E7] bg-white p-4 space-y-3 sticky top-4">
                    <label className="flex items-center gap-2 text-[12.5px]">
                        <input type="checkbox" checked={ccFirm} onChange={(e) => setCcFirm(e.target.checked)} data-testid="ec-cc" />
                        Cc firm mailbox (<b>info@nztaxation.nz</b>)
                    </label>
                    {isAdmin && (
                        <label className="flex items-center gap-2 text-[12.5px]">
                            <input type="checkbox" checked={saveAsNew} onChange={(e) => setSaveAsNew(e.target.checked)} data-testid="ec-save-version" />
                            Save edit as new template version
                        </label>
                    )}
                    <Button disabled={!picked.length || !readyCount || sending} onClick={() => doSend({ isTest: false })} className="w-full bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="ec-send-all">
                        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                        Send all ready ({readyCount})
                    </Button>
                    <Button variant="outline" disabled={!picked.length || sending} onClick={() => doSend({ isTest: true })} className="w-full" data-testid="ec-send-test">
                        <Mail className="h-3.5 w-3.5 mr-1" /> Send test to me
                    </Button>
                    {!isAdmin && (
                        <Button variant="outline" onClick={() => setReviewOpen(true)} className="w-full" data-testid="ec-review-request">
                            <Flag className="h-3.5 w-3.5 mr-1" /> No template fits — Michael review required
                        </Button>
                    )}
                </div>
            </aside>

            {previewOpen && (
                <PreviewDialog
                    open={previewOpen}
                    data={previewData}
                    canEdit={isAdmin || !!tpl?.allow_staff_edit}
                    editing={editing}
                    index={previewIdx}
                    total={picked.length}
                    onPrev={() => previewIdx > 0 && openPreviewFor(previewIdx - 1)}
                    onNext={() => previewIdx < picked.length - 1 && openPreviewFor(previewIdx + 1)}
                    onClose={() => setPreviewOpen(false)}
                    onEdit={() => setEditing(true)}
                    onSaveEdit={saveEditedBody}
                />
            )}
            {reviewOpen && (
                <ReviewRequestDialog
                    open={reviewOpen}
                    clientIds={picked.map((c) => c.id)}
                    onClose={() => setReviewOpen(false)}
                />
            )}
        </div>
    );
}

function Card({ title, subtitle, children }) {
    return (
        <section className="rounded-md border border-[#E4E4E7] bg-white">
            <header className="px-4 py-3 border-b border-[#EEEDE7]">
                <div className="font-medium text-[13px] text-[#0F1115]">{title}</div>
                {subtitle && <div className="text-[11.5px] text-[#5E636E]">{subtitle}</div>}
            </header>
            <div className="p-4">{children}</div>
        </section>
    );
}

function StatusChip({ status }) {
    const meta = {
        ready: { label: "Ready", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
        missing: { label: "Missing", cls: "bg-red-50 text-red-800 border-red-200" },
        skip: { label: "Skip", cls: "bg-zinc-50 text-zinc-700 border-zinc-200" },
    }[status] || { label: status, cls: "bg-zinc-50 text-zinc-700 border-zinc-200" };
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${meta.cls}`}>{meta.label}</span>;
}

function PerClientDates({ category, gstFrequency, fields, source, onChange, onReset, testIdBase }) {
    const freqLabel = (() => {
        if (category === "PAYE") return "PAYE — monthly";
        const f = String(gstFrequency || "two_monthly").toLowerCase().replace("-", "_");
        if (f === "monthly" || f === "1") return "GST — monthly";
        if (f === "six_monthly" || f === "6" || f === "6_monthly") return "GST — 6-monthly";
        return "GST — 2-monthly";
    })();
    const items = [
        ["period_start", "Period start"],
        ["period_end", "Period end"],
        ["due_date", "Due date"],
    ];
    return (
        <div className="mt-2 rounded-md border border-[#E4E4E7] bg-zinc-50/40 p-2" data-testid={testIdBase}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11.5px] text-[#5E636E]">
                    Dates ({freqLabel}){" "}
                    {source === "default"
                        ? <span className="text-emerald-700">· auto-filled</span>
                        : source === "manual"
                            ? <span className="text-amber-700">· edited</span>
                            : null}
                </span>
                <button
                    type="button"
                    onClick={onReset}
                    className="text-[11px] text-[#0F1115] underline hover:text-[#5E636E]"
                    data-testid={`${testIdBase}-reset`}>
                    Reset to default
                </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {items.map(([k, label]) => (
                    <div key={k}>
                        <div className="text-[10.5px] text-[#5E636E] mb-0.5">{label}</div>
                        <Input
                            value={fields[k] || ""}
                            onChange={(e) => onChange(k, e.target.value)}
                            placeholder="dd.mm.yyyy"
                            className="h-7 text-[12px]"
                            data-testid={`${testIdBase}-${k}`}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}

function PerClientAttachments({ attachments, required, onChange, testIdBase }) {
    const [drag, setDrag] = useState(false);

    async function addFiles(files) {
        const list = Array.from(files || []);
        const out = [];
        for (const f of list) {
            const buf = await f.arrayBuffer();
            const b64 = btoa(new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""));
            out.push({ name: f.name, content_type: f.type || "application/pdf", content_base64: b64 });
        }
        onChange([...(attachments || []), ...out]);
    }

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
            className={`mt-2 rounded-md border border-dashed ${drag ? "border-[#0F1115] bg-[#F7F6F2]" : "border-[#E4E4E7] bg-zinc-50/40"} p-2`}
            data-testid={`${testIdBase}-zone`}
        >
            <div className="flex items-center justify-between gap-2 text-[11.5px] text-[#5E636E]">
                <span><Paperclip className="h-3 w-3 inline" /> Drop PDF here{required ? <span className="text-red-600"> *required</span> : null}</span>
                <label className="cursor-pointer underline">
                    <input type="file" multiple accept=".pdf,application/pdf" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid={`${testIdBase}-input`} />
                    browse
                </label>
            </div>
            {(attachments || []).map((a, i) => (
                <div key={i} className="mt-1 flex items-center gap-2 text-[11.5px]">
                    <Paperclip className="h-3 w-3 text-[#5E636E]" />
                    <span className="flex-1 truncate">{a.name}</span>
                    <button onClick={() => onChange(attachments.filter((_, j) => j !== i))} className="text-[#5E636E] hover:text-red-700">
                        <Trash2 className="h-3 w-3" />
                    </button>
                </div>
            ))}
        </div>
    );
}

function PreviewDialog({ open, data, canEdit, editing, index, total, onPrev, onNext, onClose, onEdit, onSaveEdit }) {
    const [subject, setSubject] = useState(data?.subject || "");
    const [body, setBody] = useState(data?.body_rendered_plain || "");
    useEffect(() => {
        setSubject(data?.subject || "");
        setBody(data?.body_rendered_plain || "");
    }, [data?.subject, data?.body_rendered_plain]);
    if (!data) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="ec-preview">
                <DialogHeader>
                    <DialogTitle>Preview — {data.client?.name}{total > 1 ? ` (${index + 1}/${total})` : ""}</DialogTitle>
                </DialogHeader>
                <div className="rounded-md border border-[#E4E4E7] bg-zinc-50 p-3 text-[12.5px] space-y-1">
                    <div><b>To:</b> {data.to || <span className="text-red-700">missing — won't send</span>} ({data.to_name})</div>
                    {editing ? (
                        <>
                            <div><b>Subject:</b> <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-7 text-[12.5px]" /></div>
                            <Textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} className="text-[12.5px] font-mono" />
                            <div className="text-[11px] text-[#5E636E]">Edits are per-this-email unless you tick "Save as new template version" before sending.</div>
                        </>
                    ) : (
                        <>
                            <div><b>Subject:</b> {data.subject}</div>
                            {(data.unresolved_fields || []).length > 0 && (
                                <div className="rounded-md border border-red-200 bg-red-50 p-2 text-red-800 text-[11.5px]">
                                    Unresolved placeholders: <b>{data.unresolved_fields.join(", ")}</b> — fill these before sending.
                                </div>
                            )}
                            {data.duplicate_in_last_7d && (
                                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900 text-[11.5px]">
                                    <AlertTriangle className="h-3 w-3 inline mr-1" />This template was sent to this client in the last 7 days.
                                </div>
                            )}
                            <div className="rounded-md border border-[#E4E4E7] bg-white mt-2 max-h-[55vh] overflow-y-auto">
                                <div dangerouslySetInnerHTML={{ __html: data.body_html }} />
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    {total > 1 && !editing && (
                        <>
                            <Button variant="outline" disabled={index === 0} onClick={onPrev}>← Prev client</Button>
                            <Button variant="outline" disabled={index === total - 1} onClick={onNext}>Next client →</Button>
                        </>
                    )}
                    {!editing && canEdit && <Button variant="outline" onClick={onEdit} data-testid="ec-preview-edit"><FileEdit className="h-3.5 w-3.5 mr-1" />Edit this email</Button>}
                    {editing && (
                        <Button className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" onClick={() => onSaveEdit(subject, body)} data-testid="ec-preview-save-edit">
                            <Save className="h-3.5 w-3.5 mr-1" />Save edit (this email only)
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReviewRequestDialog({ open, clientIds, onClose }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    async function send() {
        setBusy(true);
        try {
            await api.post("/email-center/review", { client_ids: clientIds, reason });
            toast.success("Review request sent to Michael — no email was dispatched.");
            onClose();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="ec-review-dialog">
                <DialogHeader><DialogTitle>Request Michael's review</DialogTitle></DialogHeader>
                <p className="text-[12.5px] text-[#5E636E]">This creates a review item for <b>Michael</b> covering {clientIds.length} client(s). No email is sent.</p>
                <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why can't a template be used?" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" onClick={send} disabled={busy}>
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Flag className="h-3.5 w-3.5 mr-1" />}Create review
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Templates tab ----------

function TemplatesTab({ isAdmin }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/email-center/templates?include_archived=${showArchived}`);
            setList(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [showArchived]);
    useEffect(() => { load(); }, [load]);

    async function dup(t) { try { await api.post(`/email-center/templates/${t.id}/duplicate`); toast.success("Duplicated"); load(); } catch (e) { toast.error(formatApiError(e)); } }
    async function archive(t, flag) { try { await api.post(`/email-center/templates/${t.id}/archive?archived=${flag}`); toast.success(flag ? "Archived" : "Restored"); load(); } catch (e) { toast.error(formatApiError(e)); } }

    return (
        <div className="space-y-3" data-testid="ec-templates">
            <div className="flex justify-between items-center">
                <label className="flex items-center gap-2 text-[12.5px] text-[#5E636E]">
                    <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
                </label>
                {isAdmin && <Button onClick={() => setEditing({ name: "", category: "GST", subject: "", body: "", required_fields: [], required_attachment: false, allow_staff_edit: false })} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="ec-tpl-new"><Plus className="h-3.5 w-3.5 mr-1" />New template</Button>}
            </div>
            {loading ? <div className="py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin" /></div> : (
                <div className="rounded-md border border-[#E4E4E7] bg-white divide-y divide-[#EEEDE7]">
                    {list.map((t) => (
                        <div key={t.id} className="px-3 py-2 flex items-center gap-2" data-testid={`ec-tpl-row-${t.id}`}>
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${t.category === "GST" ? "border-sky-200 bg-sky-50 text-sky-800" : t.category === "PAYE" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-zinc-200 bg-zinc-50 text-zinc-700"}`}>{t.category}</span>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-[13px] text-[#0F1115] truncate">{t.name}{t.archived && <span className="ml-2 text-[10px] text-zinc-500">archived</span>}</div>
                                <div className="text-[11px] text-[#5E636E] truncate">{t.subject}</div>
                            </div>
                            <span className="text-[11px] text-[#5E636E]">v{t.version}</span>
                            {t.required_attachment && <Paperclip className="h-3 w-3 text-amber-700" title="Requires attachment" />}
                            {isAdmin && <>
                                <Button size="sm" variant="outline" className="h-7 text-[11.5px]" onClick={() => setEditing(t)}><FileEdit className="h-3 w-3 mr-1" />Edit</Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dup(t)} title="Duplicate"><Copy className="h-3.5 w-3.5" /></Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => archive(t, !t.archived)} title={t.archived ? "Restore" : "Archive"}><Archive className="h-3.5 w-3.5" /></Button>
                            </>}
                        </div>
                    ))}
                </div>
            )}
            {editing && (
                <TemplateEditor
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function TemplateEditor({ initial, onClose, onSaved }) {
    const [t, setT] = useState({ ...initial });
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [saving, setSaving] = useState(false);
    const subjRef = useRef(null);
    const bodyRef = useRef(null);
    const [lastFocused, setLastFocused] = useState("body");

    useEffect(() => {
        if (initial.id) api.get(`/email-center/templates/${initial.id}`).then(({ data }) => setHistory(data.versions || [])).catch(() => {});
    }, [initial.id]);

    function insertChip(field) {
        const token = `{${field}}`;
        if (lastFocused === "subject") setT({ ...t, subject: (t.subject || "") + token });
        else setT({ ...t, body: (t.body || "") + token });
    }

    function toggleReq(k) {
        const cur = t.required_fields || [];
        setT({ ...t, required_fields: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] });
    }

    async function save() {
        setSaving(true);
        try {
            const body = {
                name: t.name, category: t.category, subject: t.subject, body: t.body,
                language: t.language || "EN",
                required_fields: t.required_fields || [],
                required_attachment: !!t.required_attachment,
                allow_staff_edit: !!t.allow_staff_edit,
            };
            if (t.id) await api.patch(`/email-center/templates/${t.id}`, body);
            else await api.post(`/email-center/templates`, body);
            toast.success("Template saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    async function restoreVersion(version) {
        if (!window.confirm(`Restore version ${version}? Current version will be kept in history.`)) return;
        try {
            await api.post(`/email-center/templates/${t.id}/restore/${version}`);
            toast.success(`Restored v${version}`);
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ec-tpl-editor">
                <DialogHeader><DialogTitle>{t.id ? `Edit template — v${t.version}` : "New template"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="grid md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                            <Label>Name</Label>
                            <Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="ec-tpl-name" />
                        </div>
                        <div>
                            <Label>Category</Label>
                            <select value={t.category} onChange={(e) => setT({ ...t, category: e.target.value })} className="w-full h-9 rounded-md border border-[#E4E4E7] bg-white px-3 text-[13px]">
                                <option value="GST">GST</option><option value="PAYE">PAYE</option><option value="other">other</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {MERGE_FIELDS.map((f) => (
                            <button key={f} onClick={() => insertChip(f)} className="rounded-full border border-[#E4E4E7] bg-zinc-50 px-2 py-0.5 text-[11px] hover:bg-zinc-100" type="button">
                                {`{${f}}`}
                            </button>
                        ))}
                    </div>
                    <div>
                        <Label>Subject</Label>
                        <Input ref={subjRef} onFocus={() => setLastFocused("subject")} value={t.subject} onChange={(e) => setT({ ...t, subject: e.target.value })} data-testid="ec-tpl-subject" />
                    </div>
                    <div>
                        <Label>Body</Label>
                        <Textarea ref={bodyRef} onFocus={() => setLastFocused("body")} rows={12} value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} className="font-mono text-[12.5px]" data-testid="ec-tpl-body" />
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-[12.5px]">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!t.required_attachment} onChange={(e) => setT({ ...t, required_attachment: e.target.checked })} /> Attachment required
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!t.allow_staff_edit} onChange={(e) => setT({ ...t, allow_staff_edit: e.target.checked })} /> Allow staff to edit body
                        </label>
                    </div>
                    <div>
                        <Label>Required fields (block send if empty)</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {["period_start", "period_end", "due_date", "deadline", "amount", "missing_items"].map((k) => (
                                <button key={k} type="button" onClick={() => toggleReq(k)}
                                    className={`rounded-full border px-2 py-0.5 text-[11px] ${(t.required_fields || []).includes(k) ? "border-[#0F1115] bg-[#0F1115] text-white" : "border-[#E4E4E7] bg-white"}`}>
                                    {MANUAL_FIELD_LABELS[k]}
                                </button>
                            ))}
                        </div>
                    </div>
                    {t.id && (
                        <div className="rounded-md border border-[#E4E4E7] bg-zinc-50 p-2">
                            <button className="text-[12.5px] text-[#0F1115] flex items-center gap-1" onClick={() => setShowHistory(!showHistory)}><History className="h-3.5 w-3.5" />Version history ({history.length})</button>
                            {showHistory && (
                                <div className="mt-2 divide-y divide-[#EEEDE7]">
                                    {history.length === 0 && <div className="text-[11.5px] text-[#5E636E] py-2">No prior versions.</div>}
                                    {history.slice().reverse().map((v) => (
                                        <div key={v.version} className="py-1.5 flex items-center justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="text-[12.5px]"><b>v{v.version}</b> — {v.subject}</div>
                                                <div className="text-[11px] text-[#5E636E]">{v.saved_at ? new Date(v.saved_at).toLocaleString("en-NZ") : ""}</div>
                                            </div>
                                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => restoreVersion(v.version)}>Restore</Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" onClick={save} disabled={saving || !t.name || !t.subject || !t.body}>
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Sent log tab ----------

function SentLogTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.append("q", q);
            if (status) params.append("status", status);
            const { data } = await api.get(`/email-center/sent-log?${params}`);
            setRows(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [q, status]);
    useEffect(() => { load(); }, [load]);

    async function viewDetail(id) {
        try {
            const { data } = await api.get(`/email-center/sent-log/${id}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="space-y-3" data-testid="ec-sent">
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-[#5E636E]" />
                    <Input placeholder="Search client, subject, or email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-8 text-[13px]" />
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px]">
                    <option value="">Any status</option>
                    <option value="sent">Delivered</option>
                    <option value="failed">Failed</option>
                </select>
            </div>
            {loading ? <div className="py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin" /></div> : (
                <div className="rounded-md border border-[#E4E4E7] bg-white overflow-x-auto">
                    <table className="w-full text-[12.5px]">
                        <thead className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">
                            <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Client</th><th className="text-left px-3 py-2">Template</th><th className="text-left px-3 py-2">Subject</th><th className="text-left px-3 py-2">Status</th><th></th></tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-[#EEEDE7]" data-testid={`ec-sent-row-${r.id}`}>
                                    <td className="px-3 py-2 whitespace-nowrap">{new Date(r.sent_at).toLocaleString("en-NZ")}</td>
                                    <td className="px-3 py-2">{r.client_name_snapshot}<div className="text-[11px] text-[#5E636E]">{r.to_email}</div></td>
                                    <td className="px-3 py-2">{r.template_name_snapshot || "—"}<div className="text-[11px] text-[#5E636E]">{r.category}</div></td>
                                    <td className="px-3 py-2 max-w-md truncate">{r.subject}</td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${r.status === "sent" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`}>
                                            {r.status === "sent" ? <CheckCircle2 className="h-2.5 w-2.5" /> : <AlertTriangle className="h-2.5 w-2.5" />}
                                            {r.status === "sent" ? "Delivered" : "Failed"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => viewDetail(r.id)}>View</Button>
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5E636E]">No emails match.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
            {detail && (
                <Dialog open onOpenChange={(v) => !v && setDetail(null)}>
                    <DialogContent className="max-w-3xl max-h-[90vh]">
                        <DialogHeader><DialogTitle>{detail.subject}</DialogTitle></DialogHeader>
                        <div className="rounded-md border border-[#E4E4E7] bg-zinc-50 p-3 text-[12.5px] space-y-1">
                            <div><b>To:</b> {detail.to_email}{(detail.cc || []).length > 0 && <> · <b>Cc:</b> {(detail.cc || []).join(", ")}</>}</div>
                            <div><b>Sent by:</b> {detail.sent_by_name} · {new Date(detail.sent_at).toLocaleString("en-NZ")}</div>
                            {!!(detail.attachments_meta || []).length && <div><b>Attachments:</b> {(detail.attachments_meta || []).map((a) => a.name).join(", ")}</div>}
                            {detail.send_error && <div className="text-red-700"><b>Error:</b> {detail.send_error}</div>}
                        </div>
                        <div className="rounded-md border border-[#E4E4E7] bg-white max-h-[55vh] overflow-y-auto">
                            <div dangerouslySetInnerHTML={{ __html: detail.body_html }} />
                        </div>
                        <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Close</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
